/**
 * Except for `normalizeUrlKey`, `BACKEND_PATH_REGEX`, `isBackendPath`, `buildBackendUrl` and `isBackendUrl`, this
 * utility is intentionally not exported from the public barrel as it is used only with
 * UrlResolverService
 */

/**
 * Normalizes a URL for redirect-loop detection: path only, lower-cased, without origin, query,
 * hash or trailing slash — so `/a?x=1`, `/A/` and `https://host/a` all collapse to `/a`.
 * Shared by server.ts (SSR redirect chain) and PageRendererComponent (client redirect chain).
 * @param url an absolute URL or a path, as sent by the backend or taken from the location
 * @returns the normalized comparison key
 */
export function normalizeUrlKey(url: string): string {
  const path = url.replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0] ?? '';
  const trimmed = path.replace(/\/+$/, '').toLowerCase();
  return trimmed.length > 0 ? trimmed : '/';
}

/**
 * Strips a single leading slash so a base origin and a path join with exactly one separator.
 * @param value the raw path
 * @returns the path without a leading slash
 */
export const stripLeadingSlash = (value: string): string => (value.startsWith('/') ? value.slice(1) : value);

/** Matches any leading URI scheme, e.g. `https:`, `mailto:`, `javascript:`. */
const HAS_SCHEME = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

/** Safe schemes for navigation links, aligned with the HTML sanitizer's href allowlist. */
export const NAVIGATION_SCHEMES = /^(https?|mailto|ftp|tel|sms):/i;

/** Safe schemes for media/file URLs — only absolute http(s) is passed through unchanged. */
export const FILE_SCHEMES = /^https?:/i;

/**
 * Joins a (possibly relative) URL with an already-resolved base origin. This is the single,
 * framework-agnostic URL-resolution algorithm shared by every download/link/media path.
 *
 * Behavior:
 * - empty/blank input → `undefined`
 * - input that already carries a URI scheme → returned unchanged if its scheme is in `safeSchemes`,
 *   otherwise `undefined` (blocks `javascript:`, and — for the file allowlist — `data:`/`blob:` etc.)
 * - relative path → `${baseOrigin}/${path}` with exactly one separator, or `undefined` when
 *   `baseOrigin` is blank (caller must guard)
 *
 * @param url the raw URL, absolute or relative
 * @param baseOrigin the resolved base origin (trailing slashes are trimmed) for relative paths
 * @param safeSchemes the allowlist of schemes returned unchanged (defaults to {@link FILE_SCHEMES})
 * @returns the absolute URL, or `undefined`
 */
export function joinBaseUrl(
  url: string | undefined | null,
  baseOrigin: string | undefined | null,
  safeSchemes: RegExp = FILE_SCHEMES
): string | undefined {
  const trimmed = url?.trim();
  if (!trimmed) return undefined;
  if (HAS_SCHEME.test(trimmed)) {
    return safeSchemes.test(trimmed) ? trimmed : undefined;
  }
  const base = baseOrigin?.trim();
  if (!base) return undefined;
  return `${base.replace(/\/+$/, '')}/${stripLeadingSlash(trimmed)}`;
}

/**
 * Paths that belong to the CS3 backend (Projects3), not to this frontend: `/admin` is the backend
 * dashboard behind Forms auth (redirects to `/admin/login`), `/authorize` is the SSO entry point.
 * Mirrors the carve-out in Projects3 `Filters/ForceHostFilter.cs`, which exempts exactly these two.
 * The optional `xx-xx/` prefix catches the culture variants the CMS redirects to (e.g. `/de-de/admin`).
 */
export const BACKEND_PATH_REGEX = /^\/(?:[a-z]{2}-[a-z]{2}\/)?(?:admin|authorize)(?:\/|$)/i;

/** Leading culture segment added by the backend; stripped so CS3 applies its own culture. */
const CULTURE_PREFIX_REGEX = /^\/[a-z]{2}-[a-z]{2}(?=\/)/i;

/**
 * Reduces any input — an absolute URL, `req.originalUrl` or `window.location.pathname` — to its
 * path and query, so both helpers below behave identically regardless of the caller.
 * @param url the raw URL or path
 * @returns the path with its query string, always starting with a slash
 */
function toPathWithQuery(url: string): string {
  const withoutOrigin = url.trim().replace(/^https?:\/\/[^/]+/i, '');
  const withoutHash = withoutOrigin.split('#')[0] ?? '';
  return withoutHash.startsWith('/') ? withoutHash : `/${withoutHash}`;
}

/**
 * Checks whether a URL points at the CS3 backend instead of a CMS page.
 * @param url the raw URL or path, with or without origin and query
 * @returns true for `/admin`, `/authorize`, their subpaths and the culture-prefixed variants
 */
export function isBackendPath(url: string): boolean {
  return BACKEND_PATH_REGEX.test(toPathWithQuery(url).split('?')[0] ?? '');
}

/**
 * Builds the absolute backend URL for a backend path, keeping subpath and query string.
 * @param url the raw URL or path, with or without origin and query
 * @param backendUrl the configured `BACKEND_URL` origin
 * @returns the absolute backend URL, or undefined when `backendUrl` is not configured
 */
export function buildBackendUrl(url: string, backendUrl: string | undefined | null): string | undefined {
  // The normalized input always starts with a slash, so it can never be mistaken for a scheme.
  return joinBaseUrl(toPathWithQuery(url).replace(CULTURE_PREFIX_REGEX, ''), backendUrl);
}

/**
 * Whether a request goes to the configured backend. Compares origins rather than the raw string: a prefix
 * match would also accept a host that merely starts with the backend's, e.g. `https://backend.example.com.attacker.test`.
 * A relative url resolves against the page in the browser; on the server it has no origin and never matches.
 * @param requestUrl absolute or relative url of the request
 * @param backendUrl the configured `BACKEND_URL`, empty when the environment does not set one
 * @returns true when both resolve to the same origin
 */
export function isBackendUrl(requestUrl: string, backendUrl: string | undefined | null): boolean {
  if (!backendUrl) return false;
  const base = typeof location === 'undefined' ? undefined : location.origin;
  try {
    return new URL(requestUrl, base).origin === new URL(backendUrl, base).origin;
  } catch {
    return false;
  }
}
